import { useQueryClient } from "@tanstack/react-query";
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { selectAtom } from "jotai/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import {
	useCreateRerate,
	useUpdateShipmentRerate,
} from "~/features/rerate/hooks/use-post-rerate.ts";
import type {
	ShipmentDetailsData,
	ShipmentManagement,
} from "~/shared/schemas/shipment-management-schema.ts";

import { SHIPMENT_TOAST_MESSAGES } from "../constants/toast-messages.ts";
import {
	isEditingShipmentAtom,
	shipmentAccessorialsDraftAtom,
	shipmentCarrierDraftAtom,
	shipmentCustomerDraftAtom,
	shipmentDraftAtom,
	shipmentValidationRequestedAtom,
} from "../state/shipment-edit-atoms.ts";
import type { AugmentedShipment } from "../types/augmented-shipment";
import {
	areRerateRelevantDetailsEqual,
	determineDocGenRequired,
	determineRerateRequired,
} from "../utils/determine-rerate.ts";
import { formatSpecialInstructions } from "../utils/format-special-instructions.ts";
import { getStatusLockLabel } from "../utils/get-status-lock-label.ts";
import {
	hasShipmentValidationErrors,
	validateShipmentDraft,
} from "../utils/validate-shipment-draft.ts";
import { useShipmentNotifications } from "./use-shipment-notifications.ts";

const SORT_AND_SEGREGATE = "Sort and Segregate";

const hasInvalidSortAndSegregatePieceCounts = (
	details: ShipmentDetailsData | null | undefined,
): boolean => {
	const hasSortAndSegregate =
		(details?.pickup?.accessorials ?? []).includes(SORT_AND_SEGREGATE) ||
		(details?.delivery?.accessorials ?? []).includes(SORT_AND_SEGREGATE);
	if (!hasSortAndSegregate) {
		return false;
	}

	return (details?.freightDetails?.lineItems ?? []).some((lineItem) => {
		const pieceCount = Number(lineItem.pieceCount);
		return !Number.isFinite(pieceCount) || pieceCount <= 0;
	});
};

const NO_COMMODITIES_MESSAGE =
	"Add at least one commodity before rerating this shipment.";

const hasNoCommodities = (
	details: ShipmentDetailsData | null | undefined,
): boolean => (details?.freightDetails?.lineItems ?? []).length === 0;

const INVALID_LINEAR_FEET_OVERWRITE_MESSAGE =
	"Overwrite Calculated Linear Feet must be greater than 0. Update or clear that value and try again.";

const hasInvalidLinearFeetOverwrite = (
	details: ShipmentDetailsData | null | undefined,
): boolean => {
	const overwrite = details?.freightDetails?.overwriteCalculatedLinearFeet;
	return overwrite != null && overwrite <= 0;
};

const applyLinearFeetOverwrite = (
	details: ShipmentDetailsData,
): ShipmentDetailsData => {
	const overwrite = details.freightDetails.overwriteCalculatedLinearFeet;
	if (overwrite == null) {
		return details;
	}
	if (overwrite <= 0) {
		return {
			...details,
			freightDetails: {
				...details.freightDetails,
				overwriteCalculatedLinearFeet: null,
			},
		};
	}

	return {
		...details,
		freightDetails: {
			...details.freightDetails,
			calculatedLinearFeet: overwrite,
		},
	};
};

export const useShipmentEditSession = ({
	shipment,
	onNavigationBlocked,
	onNavigationResolved,
}: {
	shipment: AugmentedShipment;
	onNavigationBlocked: () => void;
	onNavigationResolved: () => void;
}) => {
	const [isRefetchingAfterSave, setIsRefetchingAfterSave] = useState(false);
	const [isEditing, setIsEditing] = useAtom(isEditingShipmentAtom);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useShipmentNotifications();
	const { mutateAsync: createRerateAsync, isPending: isCreatingRerate } =
		useCreateRerate();
	const {
		mutateAsync: updateShipmentRerateAsync,
		isPending: isUpdatingRerate,
	} = useUpdateShipmentRerate();
	const store = useStore();
	const setDraft = useSetAtom(shipmentDraftAtom);
	const setShipmentAccessorials = useSetAtom(shipmentAccessorialsDraftAtom);
	const setCarrierDraft = useSetAtom(shipmentCarrierDraftAtom);
	const setCustomerDraft = useSetAtom(shipmentCustomerDraftAtom);
	const setValidationRequested = useSetAtom(shipmentValidationRequestedAtom);
	const hasUnsavedChangesRef = useRef(false);
	// Snapshot of the draft values as seeded at `enterEditMode`, used to detect
	// whether the user actually changed anything before prompting to discard.
	const initialDraftSnapshotRef = useRef<{
		draft: unknown;
		accessorials: unknown;
		carrier: unknown;
		customer: unknown;
	} | null>(null);
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			hasUnsavedChangesRef.current &&
			(currentLocation.pathname !== nextLocation.pathname ||
				currentLocation.search !== nextLocation.search ||
				currentLocation.hash !== nextLocation.hash),
	);

	const clearDrafts = useCallback(() => {
		setIsEditing(false);
		setDraft(null);
		setShipmentAccessorials(null);
		setCarrierDraft(null);
		setCustomerDraft(null);
		setValidationRequested(false);
	}, [
		setCarrierDraft,
		setCustomerDraft,
		setDraft,
		setIsEditing,
		setShipmentAccessorials,
		setValidationRequested,
	]);

	const exitEditMode = useCallback(() => {
		hasUnsavedChangesRef.current = false;
		clearDrafts();
	}, [clearDrafts]);

	const enterEditMode = useCallback(() => {
		hasUnsavedChangesRef.current = true;
		setIsEditing(true);
		const shipmentDetails = structuredClone(shipment.shipmentDetails);
		setDraft({
			...shipmentDetails,
			pickup: {
				...shipmentDetails.pickup,
				// Seed the box with both stops' loaded instructions merged; only
				// pickup's merged value is what ends up saved. Address book
				// selections during editing never touch this field.
				specialInstructions: formatSpecialInstructions(
					shipmentDetails.pickup?.specialInstructions,
					shipmentDetails.delivery?.specialInstructions,
				),
			},
			additionalDetails: {
				...shipmentDetails.additionalDetails,
				agentPro:
					shipmentDetails.additionalDetails.agentPro ??
					shipment.carrier?.agentPro ??
					null,
			},
		});
		setShipmentAccessorials(structuredClone(shipment.accessorials ?? null));
		setCarrierDraft(
			shipment.carrier ? structuredClone(shipment.carrier) : null,
		);
		const customer = shipment.customer
			? structuredClone(shipment.customer)
			: null;
		if (customer) {
			const tenderedId = customer.tenderedCustomer?.customerId ?? customer.id;
			// Clear billing when it mirrors the tendered customer so the dropdown
			// starts blank and the user is prompted to make an explicit choice.
			if (customer.billingCustomer?.customerId === tenderedId) {
				customer.billingCustomer = null;
			}
		}
		setCustomerDraft(customer);
		setValidationRequested(false);
		initialDraftSnapshotRef.current = {
			draft: store.get(shipmentDraftAtom),
			accessorials: store.get(shipmentAccessorialsDraftAtom),
			carrier: store.get(shipmentCarrierDraftAtom),
			customer: store.get(shipmentCustomerDraftAtom),
		};
	}, [
		setCarrierDraft,
		setCustomerDraft,
		setDraft,
		setIsEditing,
		setShipmentAccessorials,
		setValidationRequested,
		shipment,
		store,
	]);

	const isVoided = shipment.loadAccess?.statuses?.includes("Voided") ?? false;
	const isStatusLocked = getStatusLockLabel(shipment) !== null;

	const toggleEditing = useCallback(() => {
		if (isEditing) {
			exitEditMode();
			return;
		}
		// A cancelled or status-locked shipment cannot be edited.
		if (isVoided || isStatusLocked) {
			return;
		}
		enterEditMode();
	}, [enterEditMode, exitEditMode, isEditing, isStatusLocked, isVoided]);

	const proceedBlockedNavigation = useCallback(() => {
		if (blocker.state === "blocked") {
			onNavigationResolved();
			blocker.proceed();
		}
	}, [blocker, onNavigationResolved]);

	const resetBlockedNavigation = useCallback(() => {
		if (blocker.state === "blocked") {
			blocker.reset();
		}
		onNavigationResolved();
	}, [blocker, onNavigationResolved]);

	const discardAndLeave = useCallback(() => {
		exitEditMode();
		proceedBlockedNavigation();
	}, [exitEditMode, proceedBlockedNavigation]);

	const requestValidation = useCallback(() => {
		setValidationRequested(true);
	}, [setValidationRequested]);

	const getDraft = useCallback(() => store.get(shipmentDraftAtom), [store]);
	const getShipmentAccessorials = useCallback(
		() => store.get(shipmentAccessorialsDraftAtom),
		[store],
	);
	const getCustomerDraft = useCallback(
		() => store.get(shipmentCustomerDraftAtom),
		[store],
	);
	const getCarrierDraft = useCallback(
		() => store.get(shipmentCarrierDraftAtom),
		[store],
	);

	// Compares the current drafts against the snapshot taken when edit mode was
	// entered, so cancelling only prompts to discard if something actually changed.
	const hasUnsavedChanges = useCallback(() => {
		const initial = initialDraftSnapshotRef.current;
		if (!initial) {
			return false;
		}
		return (
			JSON.stringify(store.get(shipmentDraftAtom)) !==
				JSON.stringify(initial.draft) ||
			JSON.stringify(store.get(shipmentAccessorialsDraftAtom)) !==
				JSON.stringify(initial.accessorials) ||
			JSON.stringify(store.get(shipmentCarrierDraftAtom)) !==
				JSON.stringify(initial.carrier) ||
			JSON.stringify(store.get(shipmentCustomerDraftAtom)) !==
				JSON.stringify(initial.customer)
		);
	}, [store]);

	const rerateDraftAtom = useMemo(
		() =>
			selectAtom(
				shipmentDraftAtom,
				(draft) => draft,
				areRerateRelevantDetailsEqual,
			),
		[],
	);
	const rerateRequiredAtom = useMemo(
		() =>
			atom((get) => {
				const draft = get(rerateDraftAtom);
				const shipmentAccessorials = get(shipmentAccessorialsDraftAtom);
				const customerDraft = get(shipmentCustomerDraftAtom);

				return determineRerateRequired(
					shipment.shipmentDetails,
					draft ?? undefined,
					shipment.carrier?.expDate,
					shipment.accessorials,
					shipmentAccessorials ?? shipment.accessorials,
					shipment.customer,
					customerDraft ?? shipment.customer,
				);
			}),
		[
			rerateDraftAtom,
			shipment.accessorials,
			shipment.carrier?.expDate,
			shipment.customer,
			shipment.shipmentDetails,
		],
	);
	const rerateRequired = useAtomValue(rerateRequiredAtom);

	const startRerate = useCallback(
		async (shipmentData: ShipmentDetailsData | null): Promise<void> => {
			const quoteId = shipment.quoteId;
			if (!quoteId || !shipmentData) {
				showError("Unable to rerate: this shipment has no quote id.");
				return;
			}

			try {
				const data = await createRerateAsync({
					quoteId,
					shipmentData: { ...shipmentData, needsRerate: true },
				});
				exitEditMode();
				resetBlockedNavigation();
				navigate(
					routes.shipmentRerateCarrierSelection.replace(
						":quoteId",
						String(data.id),
					),
				);
			} catch {
				showError("Unable to start a rerate. Please try again.");
			}
		},
		[
			createRerateAsync,
			exitEditMode,
			navigate,
			resetBlockedNavigation,
			shipment.quoteId,
			showError,
		],
	);

	const handleRerate = useCallback(async () => {
		const shipmentDraft = getDraft() ?? shipment.shipmentDetails;
		if (hasNoCommodities(shipmentDraft)) {
			showError(NO_COMMODITIES_MESSAGE);
			return;
		}
		if (hasInvalidLinearFeetOverwrite(shipmentDraft)) {
			showError(INVALID_LINEAR_FEET_OVERWRITE_MESSAGE);
			return;
		}
		if (hasInvalidSortAndSegregatePieceCounts(shipmentDraft)) {
			showError(
				"Sort and Segregate requires all handling unit piece counts to be greater than 0. Update those values and try again.",
			);
			return;
		}
		await startRerate(applyLinearFeetOverwrite(shipmentDraft));
	}, [getDraft, shipment.shipmentDetails, showError, startRerate]);
	const handleRerateRef = useRef(handleRerate);
	handleRerateRef.current = handleRerate;
	const onRerate = useCallback(() => {
		void handleRerateRef.current();
	}, []);

	const handleSave = useCallback(
		async ({
			leaveAfterSave = false,
		}: {
			leaveAfterSave?: boolean;
		} = {}): Promise<void> => {
			const draft = getDraft();
			if (!draft || !shipment.quoteId) {
				return;
			}
			const quoteId = shipment.quoteId;

			if (rerateRequired && hasNoCommodities(draft)) {
				showError(NO_COMMODITIES_MESSAGE);
				return;
			}

			if (hasInvalidLinearFeetOverwrite(draft)) {
				showError(INVALID_LINEAR_FEET_OVERWRITE_MESSAGE);
				return;
			}

			requestValidation();
			const validationErrors = validateShipmentDraft(draft);
			if (hasShipmentValidationErrors(validationErrors)) {
				showError("Please complete all required fields before saving.");
				return;
			}
			if (hasInvalidSortAndSegregatePieceCounts(draft)) {
				showError(
					"Sort and Segregate requires all handling unit piece counts to be greater than 0. Update those values and try again.",
				);
				return;
			}

			try {
				const shipmentAccessorials = getShipmentAccessorials();
				const customerDraft = getCustomerDraft();
				const carrierDraft = getCarrierDraft();
				const draftWithOverwriteApplied = applyLinearFeetOverwrite(draft);
				const requiresDocGen = determineDocGenRequired(
					shipment.shipmentDetails,
					draft,
					shipment.accessorials,
					shipmentAccessorials ?? shipment.accessorials,
					shipment.customer,
					customerDraft ?? shipment.customer,
					rerateRequired,
				); /* eslint-disable @typescript-eslint/no-unused-vars -- rest siblings strip client-only augmented fields and read-only server data */
				// `exceptions` and `remarks` are generated by the API and are never
				// read back by the patch endpoint, but echoing them makes them
				// caller-supplied input that the API screens for unsafe content.
				const {
					immediateActionExceptions,
					needsAcknowledgedExceptions,
					groupedRemarks,
					groupedCheckCalls,
					quoteRecall,
					exceptions,
					remarks,
					...shipmentResponse
				} = shipment;
				/* eslint-enable @typescript-eslint/no-unused-vars -- re-enable after the omit destructure */
				const mergedShipment: ShipmentManagement = {
					...shipmentResponse,
					accessorials: shipmentAccessorials ?? shipmentResponse.accessorials,
					carrier: carrierDraft ?? shipmentResponse.carrier,
					customer: customerDraft ?? shipmentResponse.customer,
					shipmentDetails: {
						...draftWithOverwriteApplied,
						additionalDetails: {
							...draftWithOverwriteApplied.additionalDetails,
							agentPro:
								draftWithOverwriteApplied.additionalDetails.agentPro ??
								shipmentResponse.carrier?.agentPro ??
								null,
						},
						needsRerate: rerateRequired,
					},
				};

				const data = await updateShipmentRerateAsync({
					quoteId,
					shipment: mergedShipment,
					requiresDocGen,
				});
				exitEditMode();

				if (rerateRequired && data.rerateQuote) {
					resetBlockedNavigation();
					navigate(
						routes.shipmentRerateCarrierSelection.replace(
							":quoteId",
							String(data.rerateQuote.id),
						),
					);
					return;
				}

				setIsRefetchingAfterSave(true);
				await queryClient.invalidateQueries({
					queryKey: ["shipment"],
					refetchType: "all",
				});
				setIsRefetchingAfterSave(false);
				showSuccess(SHIPMENT_TOAST_MESSAGES.shipmentChangesSaved);

				if (leaveAfterSave) {
					proceedBlockedNavigation();
				}
			} catch (error) {
				setIsRefetchingAfterSave(false);
				console.error("Error saving shipment:", error);
				showError(
					rerateRequired
						? "Unable to start a rerate. Please try again."
						: "Unable to save shipment. Please try again.",
				);
			}
		},
		[
			exitEditMode,
			getCarrierDraft,
			getCustomerDraft,
			getDraft,
			getShipmentAccessorials,
			navigate,
			proceedBlockedNavigation,
			queryClient,
			requestValidation,
			rerateRequired,
			resetBlockedNavigation,
			shipment,
			showError,
			showSuccess,
			updateShipmentRerateAsync,
		],
	);

	useEffect(() => {
		if (blocker.state === "blocked") {
			onNavigationBlocked();
		}
	}, [blocker.state, onNavigationBlocked]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isEditing) {
				event.preventDefault();
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [isEditing]);

	useEffect(() => clearDrafts, [clearDrafts]);

	return {
		discardAndLeave,
		exitEditMode,
		getCustomerDraft,
		getDraft,
		getShipmentAccessorials,
		handleSave,
		hasUnsavedChanges,
		isEditing,
		isCreatingRerate,
		isRefetchingAfterSave,
		isUpdatingRerate,
		onRerate,
		proceedBlockedNavigation,
		requestValidation,
		rerateRequired,
		resetBlockedNavigation,
		stayOnPage: resetBlockedNavigation,
		toggleEditing,
	};
};
